import React, { useState, useCallback } from "react";
import { gql, useQuery } from '@apollo/client';
import {
  LayoutDashboard,
  BarChart3,
  GitBranch,
  Calculator,
  Store,
  Mail,
  CheckSquare,
  Users,
  Briefcase,
  MessageSquare,
  HelpCircle,
  Menu,
  X,
  Inbox,
  DollarSign,
  BellRing,
  IdCard,
  Pencil,
} from "lucide-react";

// Reads the same currentUser fields AppHeader uses; Apollo cache de-dupes.
// Used for partner-mode detection + the partner's own coach_profile.id
// (powers the "Public Profile" link in the partner sidebar).
const SIDEBAR_USER_QUERY = gql`
  query GetSidebarUser {
    currentUser {
      id
      coach_profile {
        id
        is_partner
      }
    }
  }
`;

function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

function isActive(href) {
  if (typeof window === "undefined") return false;
  return window.location.pathname === href;
}

function NavLink({ item, onClick }) {
  const active = isActive(item.href);
  const Icon = item.icon;

  if (item.comingSoon) {
    return (
      <span
        className="flex items-center gap-3 px-3 py-2 rounded-[8px] text-muted-foreground/30 cursor-default"
        title="Coming soon"
      >
        <Icon className="w-[18px] h-[18px] shrink-0" />
        <span className="text-[14px]">{item.label}</span>
        <span className="ml-auto font-mono text-[9px] uppercase tracking-wide">Soon</span>
      </span>
    );
  }

  return (
    <a
      href={item.href}
      onClick={onClick}
      className={cn(
        "flex items-center gap-3 px-3 py-2 rounded-[8px] transition-all",
        active
          ? "bg-[hsl(var(--succeedo-orange))]/10 text-foreground border-l-2 border-[hsl(var(--succeedo-orange))]"
          : "text-muted-foreground/55 hover:bg-muted hover:text-foreground"
      )}
    >
      <Icon className="w-[18px] h-[18px] shrink-0" />
      <span className="text-[14px]">{item.label}</span>
      {item.badge && item.badge > 0 && (
        <span className="ml-auto bg-[hsl(var(--succeedo-orange))] text-white font-mono text-[10px] rounded-full px-1.5 py-0.5 min-w-[20px] text-center">
          {item.badge}
        </span>
      )}
    </a>
  );
}

function NavSection({ label, items, onNavClick }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="px-3 mb-6">
      <div className="font-mono text-[10px] uppercase text-muted-foreground/30 px-3 mb-2 tracking-wide">
        {label}
      </div>
      <div className="space-y-1">
        {items.map((item) => (
          <NavLink key={item.href} item={item} onClick={onNavClick} />
        ))}
      </div>
    </div>
  );
}

function SidebarContent({ sections, planProgress, showPlanProgress, onNavClick }) {
  return (
    <>
      {sections.map((section, idx) => (
        <React.Fragment key={section.label}>
          <NavSection label={section.label} items={section.items} onNavClick={onNavClick} />
          {idx < sections.length - 1 && <div className="h-px bg-border mb-6" />}
        </React.Fragment>
      ))}

      {/* Bottom Section */}
      <div className="mt-auto border-t border-border pt-4 pb-4 px-3">
        {/* 90-Day Plan Progress — owners only. Partners don't have an exit
            timeline; this bar is the seller's plan. */}
        {showPlanProgress && (
          <div className="mb-4 px-3">
            <div className="font-mono text-[10px] uppercase text-muted-foreground/35 mb-2 tracking-wide">
              YOUR 90-DAY PLAN
            </div>
            <div className="h-1 bg-border rounded-full overflow-hidden mb-1">
              <div
                className="h-full bg-gradient-to-r from-[hsl(var(--succeedo-orange))] to-[hsl(var(--succeedo-orange))]/70 transition-all duration-300"
                style={{ width: `${planProgress.percentage}%` }}
              />
            </div>
            <div className="font-mono text-[10px] text-muted-foreground/35 text-right">
              {planProgress.completed} OF {planProgress.total} COMPLETE
            </div>
          </div>
        )}

        {/* Help & Support */}
        <a
          href="/succeedo/help"
          onClick={onNavClick}
          className="flex items-center gap-3 px-3 py-2 rounded-[8px] text-muted-foreground/55 hover:bg-muted hover:text-foreground transition-all"
        >
          <HelpCircle className="w-[18px] h-[18px]" />
          <span className="text-[14px]">Help & Support</span>
        </a>
      </div>
    </>
  );
}

export default function AppSidebar({
  persona = "SELLER",
  isPartner: isPartnerProp,
  planProgress = { completed: 2, total: 5, percentage: 40 },
  actionPlanBadge = 3,
}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeMobile = useCallback(() => setMobileOpen(false), []);

  // Canonical partner detection — same source AppHeader uses.
  // Apollo cache de-dupes between header + sidebar.
  const { data: userData } = useQuery(SIDEBAR_USER_QUERY, { fetchPolicy: 'cache-first' });
  const coachProfile = userData?.currentUser?.coach_profile;
  const isPartner = typeof isPartnerProp === 'boolean'
    ? isPartnerProp
    : (coachProfile?.is_partner === true);
  const coachProfileId = coachProfile?.id;

  // Partner sidebar: WORK + PROFILE & EARNINGS. No 90-day plan, no
  // owner tools, no MY WORKSPACE — partners don't have an exit
  // timeline or a personal action plan; their "work" is leads and
  // engagements.
  const partnerSections = [
    {
      label: "WORK",
      items: [
        { icon: Inbox, label: "My Leads", href: "/succeedo/partner/dashboard" },
        { icon: Briefcase, label: "My Engagements", href: "/succeedo/partner/engagements" },
        { icon: MessageSquare, label: "Messages", href: "/succeedo/partner/messages" },
      ],
    },
    {
      label: "PROFILE & EARNINGS",
      items: [
        // Public Profile points at the partner's own coach_profile page;
        // hide the link until the id has loaded so we don't render a
        // broken /succeedo/partners/undefined href.
        ...(coachProfileId
          ? [{ icon: IdCard, label: "Public Profile", href: `/succeedo/partners/${coachProfileId}` }]
          : []),
        { icon: Pencil, label: "Edit Profile", href: "/succeedo/partner/profile/edit" },
        { icon: Briefcase, label: "Browse Partners", href: "/succeedo/partners" },
        { icon: DollarSign, label: "Billing", href: "/succeedo/partner/earnings" },
      ],
    },
  ];

  // Owner (seller/buyer/both) sidebar: TOOLS + MY WORKSPACE.
  // Common tools every owner persona sees.
  const commonTools = [
    { icon: LayoutDashboard, label: "Dashboard", href: "/succeedo/home" },
    { icon: BarChart3, label: "Valuation", href: "/succeedo/valuation" },
    { icon: GitBranch, label: "Deal Structure", href: "/succeedo/deal-structure" },
    { icon: Calculator, label: "Financing", href: "/succeedo/financing-calculator" },
  ];

  const dealRoomEntry      = { icon: Store,    label: "Deal Room",      href: "/succeedo/deal-room"   };
  const inquiriesEntry     = { icon: Mail,     label: "Inquiries",      href: "/succeedo/deal-room/inquiries" };
  const marketplaceEntry   = { icon: Store,    label: "Marketplace",    href: "/succeedo/marketplace" };
  const savedSearchesEntry = { icon: BellRing, label: "Saved Searches", href: "/succeedo/marketplace/saved-searches" };

  // Persona-aware composition. Sellers manage their own listings (Deal
  // Room) and review inquiries; buyers browse the marketplace and
  // manage saved searches; 'both' / nil see everything.
  const personaUpper = (persona || "").toString().toUpperCase();
  let tools;
  if (personaUpper === "SELLER") {
    tools = [...commonTools, dealRoomEntry, inquiriesEntry];
  } else if (personaUpper === "BUYER") {
    tools = [...commonTools, marketplaceEntry, savedSearchesEntry];
  } else {
    tools = [...commonTools, dealRoomEntry, inquiriesEntry, marketplaceEntry, savedSearchesEntry];
  }

  // "Find a Partner" replaces "Partner Directory" — verb form
  // disambiguates from "Your Team" and reads as the action it actually
  // is. "Your Team" replaces "My Deal Team" — the persistent advisory
  // roster is the seat block on the dashboard, and this link goes to
  // the deeper team-workspace view at /succeedo/teams.
  const workspace = [
    { icon: CheckSquare, label: "My Action Plan", href: "/succeedo/action-plan", badge: actionPlanBadge },
    { icon: Users, label: "Your Team", href: "/succeedo/teams" },
    { icon: Briefcase, label: "Find a Partner", href: "/succeedo/partners" },
    { icon: MessageSquare, label: "Messages", href: "/succeedo/messages" },
  ];

  const ownerSections = [
    { label: "TOOLS", items: tools },
    { label: "MY WORKSPACE", items: workspace },
  ];

  const sections = isPartner ? partnerSections : ownerSections;

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        type="button"
        onClick={() => setMobileOpen(!mobileOpen)}
        className="md:hidden fixed top-4 left-4 z-[60] text-muted-foreground hover:text-foreground"
        aria-label={mobileOpen ? "Close menu" : "Open menu"}
      >
        {mobileOpen ? (
          <X className="w-5 h-5" />
        ) : (
          <Menu className="w-5 h-5" />
        )}
      </button>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-background/50 z-40"
          onClick={closeMobile}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed left-0 top-14 bottom-0 w-[240px] bg-card/80 border-r border-border backdrop-blur-[20px] z-50 flex flex-col transition-transform duration-300",
          "md:translate-x-0",
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="flex-1 overflow-y-auto py-6">
          <SidebarContent
            sections={sections}
            planProgress={planProgress}
            showPlanProgress={!isPartner}
            onNavClick={closeMobile}
          />
        </div>
      </aside>
    </>
  );
}
